/**
 * VisionRenderer — plays a Making-Biscuits vision cutscene.
 *
 * The renderer translates a `VisionSequence` into a list of
 * `CutsceneStep`s, attaches the chromatic/sepia `VisionPostProcess` to
 * the scene, and delegates everything else to the existing
 * `CutsceneSystem.play()` pipeline.
 *
 * Design spec: `docs/specs/SPEC-making-biscuits.md` §4 "Vision Delivery"
 * + §7 "Visual + Audio System Design". The renderer is a thin adapter,
 * not a new scene graph — the design explicitly calls out
 * "Delegates dialogue rendering to CutsceneSystem with custom
 * DialogueStyle flag."
 *
 * Lifecycle:
 *   1. `play(sequence, replay)` fires VISION_STARTED and plays the
 *      cutscene.
 *   2. On exit, the post-process fades back to zero, SaveSystem is
 *      marked (first-time only), a toast reads "A memory stirs.", and
 *      VISION_ENDED fires.
 *   3. Disposal happens when the owning scene disposes — the renderer
 *      cleans up its post-process, but the CutsceneSystem is owned by
 *      the scene and disposed separately.
 */
import { Scene } from "@babylonjs/core/scene";
import { CutsceneSystem, CutsceneStep } from "./CutsceneSystem";
import { VisionPostProcess } from "./VisionPostProcess";
import { VisionSequence } from "../config/visions";
import { VISION } from "../config/GameConfig";
import { SaveSystem } from "./SaveSystem";
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";

export class VisionRenderer {
  private cutscene: CutsceneSystem;
  private postFx: VisionPostProcess;

  /** True while a vision is currently playing. Exposed for tests. */
  public isPlaying = false;

  constructor(scene: Scene, cutscene: CutsceneSystem) {
    this.cutscene = cutscene;
    this.postFx = new VisionPostProcess(scene);
  }

  /** Reference to the active post-process, exposed for tests. */
  get postProcess(): VisionPostProcess {
    return this.postFx;
  }

  /**
   * Play a vision sequence. `replay` signals that the player has
   * already seen this Elder's vision — playback uses the shorter
   * `replayBeats` subset and a shorter per-beat dwell.
   *
   * Subsequent plays on the same renderer are rejected while a
   * vision is in progress — the caller (MakingBiscuitsMinigame) is
   * expected to gate on the `BISCUITS_COMPLETED` event.
   */
  play(sequence: VisionSequence, replay: boolean): void {
    if (this.isPlaying) return;
    this.isPlaying = true;

    gameEvents.emit(GameEvents.VISION_STARTED, {
      elder: sequence.elder,
      replay,
    });

    const beats = replay
      ? sequence.replayBeats.map((i) => sequence.beats[i]).filter((b) => b !== undefined)
      : sequence.beats;

    const beatDwell = replay ? VISION.REPLAY_BEAT_DURATION_MS : VISION.BEAT_DURATION_MS;

    // Build the cutscene step list. Enter: ramp post-fx to full. Body:
    // render each beat as a vision-style dialogue step (even image-only
    // beats get a short pause). Exit: ramp back to zero, toast, flag save.
    const steps: CutsceneStep[] = [];

    steps.push({
      type: "callback",
      fn: () => {
        if (this.postFx.isAttached) {
          this.postFx.animateStrength(1.0, VISION.ENTER_FADE_MS);
        }
      },
    });
    steps.push({ type: "wait", duration: VISION.ENTER_FADE_MS });

    for (let i = 0; i < beats.length; i++) {
      const beat = beats[i];
      steps.push({
        type: "callback",
        fn: () => {
          gameEvents.emit(GameEvents.VISION_BEAT, {
            elder: sequence.elder,
            index: i,
            line: beat.line || undefined,
          });
        },
      });
      if (beat.line.trim().length > 0) {
        steps.push({
          type: "dialogue",
          speaker: "",
          text: beat.line,
          style: "vision",
          duration: beatDwell,
        });
      } else {
        // Image-only beat — a short pause so the player registers the
        // imagery without being yanked straight into the next line.
        steps.push({ type: "wait", duration: Math.max(1500, beatDwell * 0.6) });
      }
    }

    // Final flourish — the Elder's name appears quietly at the end of
    // the first-time viewing. Replays skip this (lore is already known).
    if (!replay) {
      steps.push({
        type: "dialogue",
        speaker: "",
        text: `— Elder ${capitalizeElder(sequence.elder)}`,
        style: "vision",
        duration: 2200,
      });
    }

    steps.push({
      type: "callback",
      fn: () => {
        if (this.postFx.isAttached) {
          this.postFx.animateStrength(0, VISION.EXIT_FADE_MS);
        }
      },
    });
    steps.push({ type: "wait", duration: VISION.EXIT_FADE_MS });

    steps.push({
      type: "callback",
      fn: () => {
        // Record the one-shot flag on first successful completion.
        if (!replay) {
          try {
            SaveSystem.getInstance().markBiscuitSeen(sequence.elder);
          } catch {
            // localStorage may be unavailable in headless tests; the
            // vision still completes gracefully.
          }
        }
        gameEvents.emit(GameEvents.HUD_TOAST, { message: VISION.EXIT_TOAST });
        gameEvents.emit(GameEvents.VISION_ENDED, { elder: sequence.elder });
        this.isPlaying = false;
      },
    });

    this.cutscene.play(steps);
  }

  /**
   * Force-stop a playing vision. Snaps the post-process to 0 and emits
   * VISION_ENDED so listeners don't deadlock. Does not mark the save
   * flag — canceled visions should not count as "seen."
   */
  stop(elder: "leo" | "gus" | "kitty"): void {
    if (!this.isPlaying) return;
    this.postFx.reset();
    this.isPlaying = false;
    gameEvents.emit(GameEvents.VISION_ENDED, { elder });
  }

  dispose(): void {
    this.postFx.dispose();
    this.isPlaying = false;
  }
}

function capitalizeElder(elder: "leo" | "gus" | "kitty"): string {
  // Three-way switch rather than `charAt(0).toUpperCase()` so the
  // "Grand Elder Leo" honorific lands correctly on the level-3 Elder.
  if (elder === "leo") return "Leo";
  if (elder === "gus") return "Gus";
  return "Kitty";
}
